/* eslint-disable @bigbinary/neeto/use-neetoui-classes */
import { memo } from "react";

import { Typography } from "@bigbinary/neeto-atoms";
import classnames from "classnames";
import { joinHyphenCase } from "neetocommons/utils/general";
import PropTypes from "prop-types";
import { omit } from "ramda";
import { Link } from "react-router-dom";

import { UNSUPPORTED_LINK_PROPS } from "./constants";

const Item = ({
  label,
  description,
  icon: Icon = null,
  path,
  href,
  dataTestid,
  className = "",
  children,
  ...otherProps
}) => {
  const commonProps = {
    "data-testid": dataTestid,
    className: classnames(
      "group flex cursor-pointer flex-col gap-y-4 rounded-lg border border-border p-6 no-underline outline-none transition-shadow duration-300 ease-in-out",
      "hover:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] hover:[&_[data-settings-heading]]:text-primary",
      "active:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] active:[&_[data-settings-heading]]:text-primary",
      "focus:border-primary focus:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] focus:[&_[data-settings-heading]]:text-primary",
      "focus-visible:border-primary focus-visible:shadow-[0px_0px_9.2px_0px_rgba(29,33,59,0.12)] focus-visible:[&_[data-settings-heading]]:text-primary",
      className
    ),
    ...omit(UNSUPPORTED_LINK_PROPS, otherProps),
  };

  const LinkElement = href ? "a" : Link;
  const linkProps = href
    ? { href, ...commonProps }
    : { to: path, ...commonProps };

  return (
    <LinkElement {...linkProps}>
      <div data-testid={`settings-item-${joinHyphenCase(label)}`}>
        <div className="mb-2.5 flex items-center gap-3">
          {Icon && <Icon className="text-muted-foreground" size={24} />}
          <Typography asChild variant="h3" weight="semibold">
            <span
              className="text-foreground text-lg leading-6 transition-colors duration-300 ease-in-out"
              data-settings-heading=""
              data-testid="settings-item-heading"
            >
              {label}
            </span>
          </Typography>
        </div>
        <Typography asChild variant="body2">
          <p
            className="text-muted-foreground"
            data-testid="settings-item-description"
          >
            {description}
          </p>
        </Typography>
      </div>
      {children && (
        <div className="border-border border-t pt-3">{children}</div>
      )}
    </LinkElement>
  );
};

Item.propTypes = {
  /**
   * The label text for the settings item.
   */
  label: PropTypes.string.isRequired,
  /**
   * A description of the settings item.
   */
  description: PropTypes.string,
  /**
   * The icon component to display. Accepts lucide-react icons.
   */
  icon: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * The internal route path for the settings item link.
   */
  path: PropTypes.string,
  /**
   * The external URL for the settings item link.
   */
  href: PropTypes.string,
  /**
   * A custom data-testid attribute for the link element.
   */
  dataTestid: PropTypes.string,
  /**
   * Additional CSS classes for the link element.
   */
  className: PropTypes.string,
  /**
   * Optional children rendered below the description, separated by a border.
   */
  children: PropTypes.node,
};

export default memo(Item);
